// Logica pura del form evento, condivisa fra la pagina Calendario globale
// (`CalendarPage`) e la tab Calendario del dettaglio team (`TeamCalendarTab`).
// Estratta da CalendarPage per non duplicare la normalizzazione — error-prone
// per via delle due alternative date (A: start/end con orario, B: solo `date`)
// che il backend rifiuta se mescolate (isNotMixedWithAB).
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { EVENT_TYPES } from "../../../enums/event";
import { IUser2 } from "../../../models/user copy";
import { ITeam } from "../../../models/Team";
import type { IEVentForm } from "./AddEventModal";

type FormWithDates = Partial<IEVentForm> & { dates?: string[] };

/** Validazione condivisa: stesso predicato usato dalla modal per abilitare "Conferma". */
export function isEventFormInvalid(form: IEVentForm): boolean {
  const isTestLike =
    form.type === EVENT_TYPES.TEST ||
    form.type === EVENT_TYPES.SYSTEM_TEST_EVENT;
  return (
    (!form.teams.length && !form.users.length) ||
    !form.date ||
    (!isTestLike && !form.startDate) ||
    (!isTestLike && !form.endDate) ||
    (!isTestLike && !form.name) ||
    (isTestLike && !form.testType)
  );
}

/**
 * Normalizza un form in un body pronto per POST/PATCH: pulizia campi test,
 * mapping teams/users → id, ricostruzione date sul giorno scelto, `date`
 * a mezzogiorno UTC per evitare shift di giorno per timezone. Estrae anche
 * l'array `dates` (multi-date Test) restituendolo a parte.
 */
export function buildEventBody(form: FormWithDates): {
  body: any;
  multiDates?: string[];
} {
  const body: any = cloneDeep(form);
  const multiDates: string[] | undefined = body.dates;
  delete body.dates;

  if (
    body.type === EVENT_TYPES.TEST ||
    body.type === EVENT_TYPES.SYSTEM_TEST_EVENT
  ) {
    delete body.name;
  } else {
    delete body.testType;
    delete body.testId;
  }

  body.users = body.users?.map((item: IUser2) => item._id);

  const startDate = dayjs(body.startDate);
  const endDate = dayjs(body.endDate);
  if (body.startDate) {
    body.startDate = dayjs(body.date)
      .hour(startDate.hour())
      .minute(startDate.minute())
      .toDate();
  } else {
    delete body.startDate;
  }
  if (body.endDate) {
    body.endDate = dayjs(body.date)
      .hour(endDate.hour())
      .minute(endDate.minute())
      .toDate();
  } else {
    delete body.endDate;
  }

  body.teams = body.teams?.map((item: ITeam) => item._id);
  if (body.date) {
    body.date = dayjs(body.date).startOf("day").add(12, "hours").toDate();
  }
  if (body.startDate && body.endDate) {
    delete body.date;
  }

  return { body, multiDates };
}

/**
 * Crea uno o più eventi da un form (solo CREATE): un evento per data quando
 * il form Test porta più date, altrimenti un singolo evento. Ogni data del
 * multi-date rispetta l'alternativa pura A o B a seconda che ci sia l'orario.
 */
export async function createEventsFromForm(
  form: FormWithDates,
  postEvent: (body: any) => Promise<unknown>,
): Promise<void> {
  const { body, multiDates } = buildEventBody(form);

  if (Array.isArray(multiDates) && multiDates.length > 0) {
    await Promise.all(
      multiDates.map((iso) => {
        const perDateBody = cloneDeep(body);
        if (perDateBody.startDate && perDateBody.endDate) {
          const s = dayjs(perDateBody.startDate);
          const e = dayjs(perDateBody.endDate);
          perDateBody.startDate = dayjs(iso)
            .hour(s.hour())
            .minute(s.minute())
            .second(0)
            .toDate();
          perDateBody.endDate = dayjs(iso)
            .hour(e.hour())
            .minute(e.minute())
            .second(0)
            .toDate();
          delete perDateBody.date;
        } else {
          perDateBody.date = dayjs(iso)
            .startOf("day")
            .add(12, "hours")
            .toDate();
          delete perDateBody.startDate;
          delete perDateBody.endDate;
        }
        return postEvent(perDateBody);
      }),
    );
    return;
  }

  await postEvent(body);
}
